import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, Button } from '@mui/material'
import { Timestamp, doc, updateDoc } from 'firebase/firestore'
import PageContent from '../common/components/page-content'
import useUser from '../common/hooks/use-user'
import BaaAgreementPanel from '../baa/baa-agreement-panel'
import { trackBaaAccepted, trackBaaPageViewed } from '../common/analytics'
import { db } from '../firebase'

function AcceptBaaPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [user, isLoadingUser] = useUser()
  const [isAccepted, setIsAccepted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const redirectTo = searchParams.get('redirectTo') ?? '/'

  useEffect(() => {
    if (isLoadingUser || !user)
      return

    if (user.baaAgreementDate)
      navigate(redirectTo, { replace: true })
  }, [isLoadingUser, user, navigate, redirectTo])

  useEffect(() => {
    trackBaaPageViewed()
  }, [])

  const handleAccept = async () => {
    if (!user)
      return

    setError(null)
    setIsSubmitting(true)

    try {
      await updateDoc(doc(db, `users/${user.id}`), { baaAgreementDate: Timestamp.now() })
      trackBaaAccepted()
      // Navigation should be handled by the useEffect above
    } catch (err) {
      setError((err as Error).message ?? 'Failed to accept BAA')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoadingUser || !user)
    return null

  if (user.baaAgreementDate)
    return null // Blank while redirecting

  return (
    <PageContent>
      <div className="mx-auto max-w-2xl px-4 py-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Business Associate Agreement
        </h2>
        <div className="text-sm text-gray-600 mb-6">
          <p>We've updated how we document acceptance of our Business Associate Agreement (BAA).</p>
          <p>To continue using NoteZap, please review and accept the BAA.</p>
        </div>

        {error && (
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
        )}

        <BaaAgreementPanel
          isAccepted={isAccepted}
          onAcceptedChange={setIsAccepted}
          isSubmitting={isSubmitting}
        />

        <Button
          variant="contained"
          fullWidth
          className="!mt-6"
          disabled={!isAccepted || isSubmitting}
          onClick={handleAccept}
        >
          Accept and continue
        </Button>
      </div>
    </PageContent>
  )
}

export default AcceptBaaPage
